import { useQuery } from '@tanstack/react-query';
import type {
  OrderOutboundIntent,
  OrderProviderEvent,
} from '@skupreme/api-contracts/core';
import { Badge, type BadgeTone, Card, Spinner } from '@skupreme/shared/ui';
import { DateUtility } from '@utility/date';
import OrdersService from '@utility/services/order';
import { orderKeys } from '../../lib/query-keys';

/* A code as words ("cancelAtProvider" -> "Cancel at provider", "outcome_unknown" -> "Outcome unknown"). */
export const codeWords = (code: string) => {
  const spaced = code
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const INTENT_TONES: Record<string, BadgeTone> = {
  accepted: 'success',
  sent: 'neutral',
  rejected: 'danger',
  exception: 'danger',
  outcome_unknown: 'warning',
};

const EVENT_TONES: Record<string, BadgeTone> = {
  shipped: 'success',
  partially_shipped: 'success',
  accepted: 'neutral',
  cancelled: 'warning',
  exception: 'danger',
};

const trackingNumbers = (event: OrderProviderEvent) =>
  event.tracking.flatMap(entry => {
    const number =
      entry && typeof entry === 'object'
        ? ((entry as Record<string, unknown>).trackingNumber ??
          (entry as Record<string, unknown>).tracking_number)
        : null;
    return typeof number === 'string' ? [number] : [];
  });

type Entry =
  | { kind: 'intent'; at: string; intent: OrderOutboundIntent }
  | { kind: 'event'; at: string; event: OrderProviderEvent };

/**
 * EN-4134: what we sent each provider about the order (accept, cancel,
 * tracking pushes: state, attempts, the provider's error) and what the
 * providers told us back (accepted, shipped with tracking, cancelled), as one
 * timeline, newest first.
 */
export function ProviderActivityCard({ orderId }: { orderId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: orderKeys.providerActivity(orderId),
    queryFn: () => OrdersService.getProviderActivity(orderId),
    retry: false,
  });

  const entries: Entry[] = [
    ...(data?.intents ?? []).map(intent => ({
      kind: 'intent' as const,
      at: intent.sentAt ?? intent.updatedAt,
      intent,
    })),
    ...(data?.events ?? []).map(event => ({
      kind: 'event' as const,
      at: event.observedAt,
      event,
    })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  const unavailable =
    isError || data?.not_supported.includes('provider_activity_unavailable');

  return (
    <Card title="Provider activity">
      {isLoading ? (
        <Spinner />
      ) : unavailable ? (
        <p className="sk:text-fg-secondary">
          Could not be read right now. Reload the page in a moment.
        </p>
      ) : entries.length === 0 ? (
        <p className="sk:text-fg-secondary">
          Nothing sent to a provider for this order yet.
        </p>
      ) : (
        <ol
          aria-label="Provider activity"
          className="sk:m-0 sk:flex sk:list-none sk:flex-col sk:gap-3 sk:p-0"
        >
          {entries.map(entry =>
            entry.kind === 'intent' ? (
              <li
                key={`intent-${entry.intent.id}`}
                className="sk:flex sk:flex-col sk:gap-0.5"
              >
                <span className="sk:flex sk:flex-wrap sk:items-center sk:gap-2 sk:text-dense sk:text-fg">
                  <span className="sk:font-medium">
                    Sent to {entry.intent.providerName ?? 'provider'}:{' '}
                    {codeWords(entry.intent.operation)}
                  </span>
                  <Badge tone={INTENT_TONES[entry.intent.state] ?? 'neutral'}>
                    {codeWords(entry.intent.state)}
                  </Badge>
                  {entry.intent.attempts > 1 ? (
                    <span className="sk:text-fg-tertiary">
                      {entry.intent.attempts} attempts
                    </span>
                  ) : null}
                  <span className="sk:text-fg-tertiary">
                    {DateUtility.formatDate(entry.at)}
                  </span>
                </span>
                {entry.intent.providerOrderRef ? (
                  <span className="sk:text-dense sk:text-fg-secondary">
                    Provider order {entry.intent.providerOrderRef}
                  </span>
                ) : null}
                {entry.intent.lastError ? (
                  <span className="sk:text-dense sk:text-danger sk:wrap-anywhere">
                    {entry.intent.lastError}
                  </span>
                ) : null}
              </li>
            ) : (
              <li
                key={`event-${entry.event.id}`}
                className="sk:flex sk:flex-col sk:gap-0.5"
              >
                <span className="sk:flex sk:flex-wrap sk:items-center sk:gap-2 sk:text-dense sk:text-fg">
                  <span className="sk:font-medium">
                    From {entry.event.providerName ?? 'provider'}
                  </span>
                  <Badge tone={EVENT_TONES[entry.event.status] ?? 'neutral'}>
                    {codeWords(entry.event.status)}
                  </Badge>
                  <span className="sk:text-fg-tertiary">
                    {DateUtility.formatDate(entry.at)}
                  </span>
                </span>
                {trackingNumbers(entry.event).length ? (
                  <span className="sk:text-dense sk:text-fg-secondary">
                    {entry.event.carrier ? `${entry.event.carrier} ` : ''}
                    {trackingNumbers(entry.event).join(', ')}
                  </span>
                ) : null}
              </li>
            ),
          )}
        </ol>
      )}
    </Card>
  );
}
